/**
 * Compliance → Portability - Phase 8 batch E item 1 (2026-05-17).
 *
 * GDPR Art 20 right to data portability. Fourth in the subject-rights
 * quartet. The Big Difference: on success, the job produces a BUNDLE
 * artifact (signed URL + SHA-256 + TTL) the subject can download. The
 * detail modal exposes the bundle metadata so the operator can hand
 * the subject a verifiable artifact pointer.
 *
 * Mirrors the Erasure / Rectification / Restriction page layout -
 * KPI bento + submit form + queue + detail modal - so the DPO console
 * has consistent muscle memory across all four workflows.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Plus,
  ShieldCheck,
  Play,
  Eye,
  Ban,
  Download,
  Hourglass,
  FileText,
  Hash,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listPortabilityJobs,
  createPortabilityRequest,
  approvePortabilityJob,
  executePortabilityJob,
  type PortabilityJob,
  type PortabilityJobStatus,
  type PortabilityIdentifierKind,
  type BundleFormat,
} from '@/lib/api';

const STATUS_VARIANT: Record<PortabilityJobStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  pending: 'glass',
  approved: 'warning',
  running: 'warning',
  completed: 'success',
  expired: 'glass',
  failed: 'critical',
  cancelled: 'critical',
};

const STATUS_ICON: Record<PortabilityJobStatus, typeof CheckCircle2> = {
  pending: Clock,
  approved: ShieldCheck,
  running: Loader2,
  completed: Download,
  expired: Hourglass,
  failed: XCircle,
  cancelled: Ban,
};

const IDENTIFIER_OPTIONS: ReadonlyArray<{ value: PortabilityIdentifierKind; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'pan', label: 'PAN' },
  { value: 'customer_number', label: 'Customer number' },
  { value: 'custom', label: 'Custom identifier' },
];

const FORMAT_OPTIONS: ReadonlyArray<{ value: BundleFormat; label: string; hint: string }> = [
  { value: 'json', label: 'JSON', hint: 'Canonical, machine-readable. Default per Art 20.' },
  { value: 'csv', label: 'CSV', hint: 'Tabular; pivot-friendly. Compact for finance / CRM exports.' },
  { value: 'xml', label: 'XML', hint: 'Older systems with schema-driven ingest pipelines.' },
];

function formatBytes(n: number | null): string {
  if (n == null) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function timeUntil(iso: string | null): string {
  if (!iso) return '-';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3600_000);
  const m = Math.floor((ms % 3600_000) / 60_000);
  if (h >= 24) return `~${Math.floor(h / 24)}d ${h % 24}h`;
  return `${h}h ${m}m`;
}

export function CompliancePortability(): JSX.Element {
  const [jobs, setJobs] = useState<PortabilityJob[]>([]);
  const [counts, setCounts] = useState({
    pending: 0, approved: 0, running: 0, completed: 0, expired: 0, failed: 0, cancelled: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [identifierKind, setIdentifierKind] = useState<PortabilityIdentifierKind>('email');
  const [principalIdentifier, setPrincipalIdentifier] = useState('');
  const [bundleFormat, setBundleFormat] = useState<BundleFormat>('json');
  const [requestReason, setRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [detailJob, setDetailJob] = useState<PortabilityJob | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listPortabilityJobs({});
      setJobs(r.jobs);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load portability jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleSubmit(): Promise<void> {
    if (!principalIdentifier.trim()) {
      setError('Principal identifier is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createPortabilityRequest({
        principalIdentifier: principalIdentifier.trim(),
        identifierKind,
        bundleFormat,
        ...(requestReason.trim() ? { requestReason: requestReason.trim() } : {}),
      });
      setPrincipalIdentifier('');
      setRequestReason('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to submit portability request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApprove(j: PortabilityJob): Promise<void> {
    const reason = window.prompt(`Approve portability for principal ${j.principalIdentifierHash.slice(0, 12)}…?\nApproval reason:`);
    if (!reason) return;
    try {
      await approvePortabilityJob(j.id, reason);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'approve failed');
    }
  }

  async function handleExecute(j: PortabilityJob): Promise<void> {
    if (!window.confirm(
      `Generate ${j.bundleFormat.toUpperCase()} bundle across ${j.assetsScope.length} asset(s)? ` +
      `The bundle URL has a 72-hour TTL by default; the subject must download within that window.`,
    )) return;
    try {
      await executePortabilityJob(j.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'execute failed');
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Right to data portability (GDPR Art 20)"
        subtitle="Generate machine-readable bundles of a subject's personal data. Per-asset extraction logged for the regulator audit; the bundle SHA-256 lets the subject verify the file they downloaded matches what we logged."
        badges={[
          { label: 'DPO-gated', variant: 'glass' },
          { label: 'SHA-256 bundle hash', variant: 'glass' },
          { label: '72-hour signed URL TTL', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Portability action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Pending" value={counts.pending} icon={Clock} glow={counts.pending > 0 ? 'medium' : 'low'} hint="awaiting approval" />
        <KpiCard label="Approved" value={counts.approved} icon={ShieldCheck} glow={counts.approved > 0 ? 'medium' : 'low'} hint="ready to generate" />
        <KpiCard label="Completed" value={counts.completed} icon={Download} glow={counts.completed > 0 ? 'high' : 'low'} hint="bundle ready" />
        <KpiCard label="Expired" value={counts.expired} icon={Hourglass} glow="low" hint="TTL elapsed" />
        <KpiCard label="Failed" value={counts.failed} icon={XCircle} glow={counts.failed > 0 ? 'critical' : 'low'} hint="see execution log" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Submit portability request
            </CardTitle>
            <CardDescription>
              Identifier is SHA-256 hashed before persistence. On execute, a deterministic bundle
              manifest is built across in-scope assets; its SHA-256 is recorded so the subject can
              verify their download.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-muted-foreground block mb-0.5">Identifier kind</label>
              <select
                value={identifierKind}
                onChange={(e) => setIdentifierKind(e.target.value as PortabilityIdentifierKind)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {IDENTIFIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Principal identifier</label>
              <input
                type="text"
                value={principalIdentifier}
                onChange={(e) => setPrincipalIdentifier(e.target.value)}
                placeholder="value"
                maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Bundle format</label>
              <select
                value={bundleFormat}
                onChange={(e) => setBundleFormat(e.target.value as BundleFormat)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {FORMAT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {FORMAT_OPTIONS.find((f) => f.value === bundleFormat)?.hint}
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Reason (optional)</label>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Subject exercised Art 20 right via portal"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div className="flex justify-end pt-1">
              <Button size="sm" onClick={() => void handleSubmit()} disabled={submitting}>
                {submitting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Download className="h-3.5 w-3.5 mr-1" />}
                Submit request
              </Button>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Portability queue</CardTitle>
            <CardDescription>
              <strong>pending → approved → running → completed → expired</strong>. Each transition
              is audit-chain logged. Expired bundles cannot be re-downloaded - the subject must
              submit a fresh request.
            </CardDescription>
          </CardHeader>
          {loading && (
            <LoadingState caption="Loading…" />
          )}
          {!loading && jobs.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Download className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <div>No portability jobs yet.</div>
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div className="space-y-2">
              {jobs.map((j, i) => {
                const SIcon = STATUS_ICON[j.status];
                return (
                  <motion.div
                    key={j.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="glass-lite rounded p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="glass" className="text-[10px] font-mono">{j.identifierKind}</Badge>
                          <Badge variant={STATUS_VARIANT[j.status]} className="text-[10px] inline-flex items-center gap-1">
                            <SIcon className={`h-3 w-3 ${j.status === 'running' ? 'animate-spin' : ''}`} />
                            {j.status}
                          </Badge>
                          <Badge variant="glass" className="text-[10px] uppercase">{j.bundleFormat}</Badge>
                          <span className="text-[10px] text-muted-foreground">{j.assetsScope.length} asset(s)</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" title={j.principalIdentifierHash}>
                          {j.principalIdentifierHash.slice(0, 24)}…
                        </div>
                        {j.bundleSha256 && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            bundle: <code className="font-mono">{j.bundleSha256.slice(0, 16)}…</code>
                            <span className="ml-1">· {formatBytes(j.bundleSizeBytes)}</span>
                            <span className="ml-1">· TTL {timeUntil(j.expiresAt)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" variant="glass" onClick={() => setDetailJob(j)} title="Details">
                          <Eye className="h-3 w-3" />
                        </Button>
                        {j.status === 'pending' && (
                          <Button size="sm" variant="glass" onClick={() => void handleApprove(j)} title="Approve">
                            <ShieldCheck className="h-3 w-3" />
                          </Button>
                        )}
                        {j.status === 'approved' && (
                          <Button size="sm" variant="glass" onClick={() => void handleExecute(j)} title="Generate bundle">
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </div>

      <Dialog open={detailJob !== null} onOpenChange={(o) => { if (!o) setDetailJob(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Portability job detail</DialogTitle>
          </DialogHeader>
          {detailJob && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Status</div>
                  <Badge variant={STATUS_VARIANT[detailJob.status]} className="text-[10px]">{detailJob.status}</Badge>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Bundle format</div>
                  <code className="font-mono uppercase">{detailJob.bundleFormat}</code>
                </div>
                <div className="glass-lite rounded p-2 col-span-2">
                  <div className="text-muted-foreground mb-0.5">Principal hash</div>
                  <code className="font-mono text-[10px] break-all">{detailJob.principalIdentifierHash}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Completed</div>
                  <code className="font-mono">{detailJob.completedAt ? new Date(detailJob.completedAt).toLocaleString() : '-'}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">TTL remaining</div>
                  <code className="font-mono">{timeUntil(detailJob.expiresAt)}</code>
                </div>
              </div>

              {detailJob.bundleSha256 && (
                <div className="glass-lite rounded p-3 space-y-1.5">
                  <div className="font-medium flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Bundle artifact</div>
                  <div className="flex items-start gap-2">
                    <Hash className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] text-muted-foreground">SHA-256</div>
                      <code className="font-mono text-[10px] break-all">{detailJob.bundleSha256}</code>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Download className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] text-muted-foreground">Signed URL</div>
                      <code className="font-mono text-[10px] break-all">{detailJob.bundleUrl}</code>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Signed URL is valid for 7 days (DPDPA §11 + GDPR Art 20 retrieval window). Bundle is generated server-side and uploaded to your tenant's S3 evidence vault.
                      </div>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    Size: {formatBytes(detailJob.bundleSizeBytes)} · Expires{' '}
                    {detailJob.expiresAt ? new Date(detailJob.expiresAt).toLocaleString() : '-'}
                  </div>
                </div>
              )}

              {detailJob.approverActorId && (
                <div className="glass-lite rounded p-3">
                  <div className="font-medium mb-1">Approval</div>
                  <div className="text-muted-foreground">
                    By <code className="font-mono">{detailJob.approverActorId.slice(0, 8)}…</code>
                    {detailJob.approverDecidedAt && <> at {new Date(detailJob.approverDecidedAt).toLocaleString()}</>}
                  </div>
                  {detailJob.approverReason && (
                    <div className="mt-1">Reason: <em>{detailJob.approverReason}</em></div>
                  )}
                </div>
              )}

              <div>
                <div className="font-medium mb-1">Assets in scope ({detailJob.assetsScope.length})</div>
                {detailJob.assetsScope.length === 0 ? (
                  <div className="glass-lite rounded p-3 text-muted-foreground italic">
                    The access-graph walk found no assets touching this principal.
                  </div>
                ) : (
                  <div className="glass-lite rounded p-2 max-h-32 overflow-y-auto">
                    {detailJob.assetsScope.map((id) => (
                      <code key={id} className="block font-mono text-[10px]">{id}</code>
                    ))}
                  </div>
                )}
              </div>

              {detailJob.executionLog.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Execution log</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detailJob.executionLog.map((e, i) => (
                      <div key={i} className="glass-lite rounded p-2 flex items-start gap-2">
                        <Badge
                          variant={e.status === 'ok' ? 'success' : e.status === 'failed' ? 'critical' : 'warning'}
                          className="text-[9px] shrink-0"
                        >
                          {e.status}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[10px] text-muted-foreground">{e.assetId}</div>
                          <div>{e.detail}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {new Date(e.ts).toLocaleTimeString()} · {e.action} · {e.recordsExtracted} record(s)
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailJob.errorDetail && (
                <div className="glass-lite border border-sev-critical/40 rounded p-2 text-sev-critical">
                  <div className="font-medium">Error</div>
                  <div>{detailJob.errorDetail}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
